import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api } from "../lib/api";
import { Terminal, LayoutDashboard, FlaskConical, BarChart3, LogOut, User, Sun, Moon, Crown, Zap, ShieldCheck } from "lucide-react";
import BugReportButton from "./BugReportButton";
import PoolStatusPill from "./PoolStatusPill";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  // Lightweight billing-status read so we can swap "Upgrade" → Pro badge in the
  // navbar. Errors silently ignored — navbar still renders without a billing pill.
  const [tier, setTier] = useState(null);
  useEffect(() => {
    if (!user || user === false) {
      setTier(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/status");
        if (!cancelled) setTier(data.tier);
      } catch {
        if (!cancelled) setTier(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const linkClass = ({ isActive }) =>
    `px-3 py-1.5 text-sm tracking-tight transition-colors flex items-center gap-2 border-b-2 ${
      isActive
        ? "border-[#00E5FF] text-white"
        : "border-transparent text-zinc-400 hover:text-zinc-200"
    }`;

  return (
    <header
      className="sticky top-0 z-40 border-b border-zinc-800 bg-[#0A0A0A]/90 backdrop-blur-md"
      data-testid="app-navbar"
    >
      <div className="max-w-[1800px] mx-auto flex items-center justify-between px-4 sm:px-6 h-14">
        <Link
          to="/"
          className="flex items-center gap-2 group cursor-pointer hover:opacity-90 transition-opacity"
          title="Back to home"
          data-testid="logo-link"
        >
          <div className="w-7 h-7 flex items-center justify-center bg-[#00E5FF] text-black group-hover:scale-110 transition-transform">
            <Terminal size={16} strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-lg tracking-tight">
            Hypex<span className="text-[#00E5FF]">Labs</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard">
            <LayoutDashboard size={14} /> Dashboard
          </NavLink>
          <NavLink to="/labs" className={linkClass} data-testid="nav-labs">
            <FlaskConical size={14} /> Labs
          </NavLink>
          <NavLink to="/progress" className={linkClass} data-testid="nav-progress">
            <BarChart3 size={14} /> Progress
          </NavLink>
          {user && user !== false && (user.email || "").toLowerCase() === "admin@ciscolab.ai" && (
            <NavLink to="/admin/labs" className={linkClass} data-testid="nav-admin-labs">
              <ShieldCheck size={14} /> Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user && user !== false && (user.email || "").toLowerCase() === "admin@ciscolab.ai" && (
            <PoolStatusPill />
          )}
          {user && user !== false && tier === "pro" && (
            <Link
              to="/pricing"
              className="hidden sm:flex items-center gap-1 px-2 py-1 bg-[#00E5FF]/15 border border-[#00E5FF]/50 text-[#00E5FF] text-[10px] font-mono uppercase tracking-[0.2em] font-bold hover:bg-[#00E5FF]/25 transition-colors"
              data-testid="nav-pro-badge"
              title="Manage subscription"
            >
              <Crown size={11} /> pro
            </Link>
          )}
          {user && user !== false && tier === "free" && (
            <Link
              to="/pricing"
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-[#00E5FF] text-black text-[10px] font-mono uppercase tracking-[0.2em] font-bold hover:bg-[#00B8CC] transition-colors"
              data-testid="nav-upgrade-btn"
              title="Upgrade to Pro"
            >
              <Zap size={11} /> upgrade
            </Link>
          )}
          {user && user !== false && (
            <div className="hidden sm:flex flex-col items-end gap-0.5" data-testid="user-email">
              <div className="flex items-center gap-2 text-xs text-zinc-400 font-mono">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name || user.email}
                    className="w-6 h-6 rounded-full object-cover border border-zinc-700"
                    referrerPolicy="no-referrer"
                    data-testid="user-avatar"
                  />
                ) : (
                  <User size={13} />
                )}
                {user.email}
              </div>
              <BugReportButton />
            </div>
          )}
          <button
            onClick={toggle}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="text-zinc-400 hover:text-white text-xs uppercase tracking-widest font-mono flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-800 hover:border-zinc-600 transition-colors"
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun size={13} /> : <Moon size={13} />}
            <span className="hidden sm:inline">{theme === "dark" ? "Light mode" : "Dark mode"}</span>
          </button>
          <button
            onClick={async () => {
              await logout();
              nav("/");
            }}
            className="text-zinc-400 hover:text-white text-xs uppercase tracking-widest font-mono flex items-center gap-1"
            data-testid="logout-button"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
      </div>
    </header>
  );
}
